import React, { useState, useEffect } from 'react';
import { editAutoAndManualScores } from '../../services/evaluationService';

const fieldLabels = {
  cwe_a1_01_valid_due_date: 'Valid Due Date',
  cwe_a1_02_valid_word_count: 'Valid Word Count',
  cwe_a1_03_valid_language: 'Valid Language',
  cwe_a1_04_valid_plagiarism: 'Valid Plagiarism',
  cwe_a1_05_valid_content_relevance: 'Valid Content Relevance',
  cwe_a2_01_formatting_score: 'Formatting Score (Max 20)',
  cwe_a2_02_grammar_score: 'Grammar Score (Max 25)',
  cwe_a2_03_seo_score: 'SEO Score (Max 20)',
  cwe_m_01_manual_score: 'Manual Score (Max 35)',
};

const scoreValidations = {
  cwe_a2_01_formatting_score: 20,
  cwe_a2_02_grammar_score: 25,
  cwe_a2_03_seo_score: 20,
  cwe_m_01_manual_score: 35,
};

const OrderEditScore = ({ order, onCancelEdit, onSaveSuccess }) => {
  console.log('🟡 Incoming Order:', order);

  if (!order || !order.order_id) {
    console.error('❌ Invalid order passed to OrderEditScore');
    return <div className="p-4 text-red-600">Invalid order data.</div>;
  }

  const original = order;

  const [proposed, setProposed] = useState({ ...original });

  // Only run on first load, prevent overwriting user's edits
  useEffect(() => {
    console.log('🔄 Setting initial proposed values');
    setProposed({ ...original });
  }, []);

  const handleChange = (field, value) => {
    console.log(`✏️ Changing ${field} to:`, value);

    // Number validation
    if (field in scoreValidations) {
      const max = scoreValidations[field];
      const numeric = parseFloat(value);
      if (isNaN(numeric) || numeric < 0 || numeric > max) {
        alert(`Please enter a number between 0 and ${max} for ${fieldLabels[field]}`);
        return;
      }
      setProposed(prev => ({ ...prev, [field]: numeric }));
      return;
    }

    // Boolean OR null update
    if (field.startsWith('cwe_a1')) {
      setProposed(prev => ({ ...prev, [field]: value }));
      return;
    }
  };

  const handleSubmit = async () => {
    const payload = { ...proposed };
    delete payload.final_score;

    console.log('📤 Submitting updated scores for order:', order.order_id);
    console.log('🧾 Payload:', payload);

    try {
      const response = await editAutoAndManualScores(order.order_id, payload);
      console.log('✅ Server response:', response);

      if (response.success) {
        alert('Scores updated successfully');
        onSaveSuccess();
      } else {
        alert('Error updating scores');
      }
    } catch (error) {
      console.error('❌ Error submitting scores:', error);
      alert('Error submitting scores');
    }
  };

  const renderFieldRow = (field) => {
    const isBoolean = field.startsWith('cwe_a1');
    return (
      <tr key={field} className="border-b">
        <td className="p-2 font-medium">{fieldLabels[field]}</td>

        {/* CURRENT VALUE */}
        <td className="p-2 text-center">
          {isBoolean
            ? original[field] === true
              ? '✅'
              : original[field] === false
              ? '❌'
              : '-'
            : original[field] ?? '-'}
        </td>

        {/* PROPOSED VALUE */}
        <td className="p-2 text-center">
          {isBoolean ? (
            <select
              value={
                proposed[field] === true
                  ? 'true'
                  : proposed[field] === false
                  ? 'false'
                  : ''
              }
              onChange={(e) => {
                const val = e.target.value;
                handleChange(
                  field,
                  val === '' ? null : val === 'true'
                );
              }}
              className="border rounded px-2 py-1"
            >
              <option value="">-- Not Set --</option>
              <option value="true">Valid</option>
              <option value="false">Invalid</option>
            </select>
          ) : (
            <input
              type="number"
              className="border rounded px-2 py-1 w-24"
              value={proposed[field] ?? ''}
              onChange={(e) => handleChange(field, e.target.value)}
              max={scoreValidations[field]}
            />
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="p-6 bg-white border rounded shadow">
      <h3 className="text-xl font-bold mb-4">Edit Order Evaluation</h3>

      <table className="w-full table-auto border-collapse text-sm">
        <thead>
          <tr className="bg-gray-200">
            <th className="p-2 text-left">Field</th>
            <th className="p-2 text-center">Current Evaluation</th>
            <th className="p-2 text-center">Proposed Evaluation</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(fieldLabels).map(renderFieldRow)}
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded"
          onClick={onCancelEdit}
        >
          Cancel
        </button>

        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={handleSubmit}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default OrderEditScore;
